import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Menu, Home, PlaySquare, Radio, MessageSquare, MapPin, Users,
  Bell, User as UserIcon, Shield, Coins, Video, Feather, LogOut, X, Ticket, BarChart3, Activity, Crown, HelpCircle, Scale,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BecomeCreatorButton } from "./CreatorModeToggle";
import { isNativeIOS } from "@/lib/nativePush";
import SidePill from "./SidePill";

const NAV_ITEMS = [
  { to: "/feed",          label: "Feed",          icon: Home },
  { to: "/members",       label: "Members",       icon: Users },
  { to: "/live",          label: "Live",          icon: Radio },
  { to: "/inbox",         label: "Inbox",         icon: MessageSquare },
  { to: "/nearby",        label: "Discover",      icon: MapPin },
  { to: "/communities",   label: "Communities",   icon: Users },
  { to: "/notifications", label: "Notifications", icon: Bell },
  { to: "/vip",           label: "VIP & Badges",  icon: Crown },
  { to: "/tutorial",      label: "How to use",    icon: HelpCircle },
  { to: "/legal",         label: "Legal & Support", icon: Scale },
];

/**
 * Mobile-only sticky top header + slide-out drawer.
 * Gives phone users access to EVERY nav item — Admin, Profile, Reels, Inbox,
 * Coins, Go Live, Logout — that were previously hidden in the desktop-only
 * left rail. Hidden on lg+ where the LeftRail takes over.
 */
export default function MobileHeader({ onCompose, onGoLive, onTicket }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const close = () => setOpen(false);
  const go = (path) => { close(); navigate(path); };

  return (
    <header
      className="lg:hidden sticky top-0 z-30 glass border-b border-white/10 flex items-center justify-between px-4 py-3"
      style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}
      data-testid="mobile-header"
    >
      <NavLink to="/feed" className="flex items-center gap-2 min-w-0">
        <div className="w-8 h-8 rounded-full bg-rose-600 flex items-center justify-center text-white font-bold shrink-0">
          🐼
        </div>
        <span className="font-display text-lg truncate">My Panda X</span>
      </NavLink>

      <div className="flex items-center gap-2">
        {user && <SidePill compact data-testid="mobile-side-pill" />}
        {user && (
          <NavLink
            to="/coins"
            className="hidden sm:flex items-center gap-1.5 text-xs text-amber-400 border border-amber-400/40 px-2.5 py-1.5"
            data-testid="mobile-coins-pill"
          >
            <Coins size={12} /> {(user.coins ?? 0).toLocaleString()}
          </NavLink>
        )}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              className="p-2 text-zinc-200 hover:text-white"
              aria-label="Open menu"
              data-testid="mobile-menu-button"
            >
              <Menu size={22} />
            </button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="glass border-l border-white/10 w-[85%] sm:w-96 p-0 flex flex-col overflow-y-auto"
            style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
            data-testid="mobile-menu-drawer"
          >
            <SheetHeader className="px-5 py-3 border-b border-white/5 flex flex-row items-center justify-between">
              <SheetTitle className="font-display text-xl">Menu</SheetTitle>
              <button
                onClick={close}
                className="w-11 h-11 -mr-2 flex items-center justify-center text-zinc-400 hover:text-white"
                aria-label="Close menu"
                data-testid="mobile-menu-close"
              >
                <X size={22} />
              </button>
            </SheetHeader>

            {user && (
              <div className="px-5 py-4 border-b border-white/5">
                <NavLink to={`/u/${user.id}`} onClick={close} className="flex items-center gap-3">
                  <Avatar className="w-12 h-12 border border-white/10">
                    <AvatarImage src={user.avatar_url} />
                    <AvatarFallback className="bg-rose-600 text-white">
                      {user.display_name?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{user.display_name}</div>
                    <div className="text-xs text-zinc-500 truncate">{user.display_name}</div>
                  </div>
                </NavLink>
                <NavLink
                  to="/coins"
                  onClick={close}
                  className="mt-3 flex items-center justify-between px-3 py-2 border-l-2 border-amber-400 bg-black/30"
                  data-testid="mobile-drawer-coins"
                >
                  <span className="flex items-center gap-2 text-sm">
                    <Coins size={14} className="text-amber-400" />
                    {(user.coins ?? 0).toLocaleString()} coins
                  </span>
                  <span className="text-[11px] uppercase tracking-widest text-rose-500">+ Top up</span>
                </NavLink>
              </div>
            )}

            <nav className="px-2 py-2 flex-1" data-testid="mobile-drawer-nav">
              {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={close}
                  data-testid={`mobile-nav-${to.slice(1)}`}
                  className={({ isActive }) =>
                    `flex items-center gap-4 px-4 py-3 border-l-2 transition-colors ${
                      isActive
                        ? "border-rose-600 text-white bg-white/[0.03]"
                        : "border-transparent text-zinc-400 hover:text-white"
                    }`
                  }
                >
                  <Icon size={20} strokeWidth={1.6} />
                  <span className="text-sm">{label}</span>
                </NavLink>
              ))}
              {user && (
                <NavLink
                  to={`/u/${user.id}`}
                  onClick={close}
                  data-testid="mobile-nav-profile"
                  className={({ isActive }) =>
                    `flex items-center gap-4 px-4 py-3 border-l-2 transition-colors ${
                      isActive
                        ? "border-rose-600 text-white bg-white/[0.03]"
                        : "border-transparent text-zinc-400 hover:text-white"
                    }`
                  }
                >
                  <UserIcon size={20} strokeWidth={1.6} />
                  <span className="text-sm">Profile</span>
                </NavLink>
              )}
              {user?.role === "admin" && (
                <NavLink
                  to="/admin"
                  onClick={close}
                  data-testid="mobile-nav-admin"
                  className={({ isActive }) =>
                    `flex items-center gap-4 px-4 py-3 border-l-2 transition-colors ${
                      isActive
                        ? "border-rose-600 text-white bg-white/[0.03]"
                        : "border-transparent text-rose-500 hover:text-white"
                    }`
                  }
                >
                  <Shield size={20} strokeWidth={1.6} />
                  <span className="text-sm">Admin</span>
                </NavLink>
              )}
              {(user?.is_creator || user?.role === "admin") && (
                <NavLink
                  to="/creator"
                  onClick={close}
                  data-testid="mobile-nav-creator"
                  className={({ isActive }) =>
                    `flex items-center gap-4 px-4 py-3 border-l-2 transition-colors ${
                      isActive
                        ? "border-rose-600 text-white bg-white/[0.03]"
                        : "border-transparent text-amber-400 hover:text-white"
                    }`
                  }
                >
                  <BarChart3 size={20} strokeWidth={1.6} />
                  <span className="text-sm">Creator</span>
                </NavLink>
              )}
              {user && !(user.is_creator || user.role === "admin") && <BecomeCreatorButton onDone={close} />}
              {isNativeIOS() && user?.role === "admin" && (
                <NavLink
                  to="/native-check"
                  onClick={close}
                  data-testid="mobile-nav-native-check"
                  className={({ isActive }) =>
                    `flex items-center gap-4 px-4 py-3 border-l-2 transition-colors ${
                      isActive
                        ? "border-rose-600 text-white bg-white/[0.03]"
                        : "border-transparent text-emerald-400 hover:text-white"
                    }`
                  }
                >
                  <Activity size={20} strokeWidth={1.6} />
                  <span className="text-sm">Native Check</span>
                </NavLink>
              )}
            </nav>

            {user && (
              <div className="px-5 py-4 border-t border-white/5 space-y-2">
                <Button
                  onClick={() => { close(); onCompose && onCompose(); }}
                  className="btn-crimson w-full rounded-none py-5 gap-2"
                  data-testid="mobile-drawer-post"
                >
                  <Feather size={16} /> Post
                </Button>
                <Button
                  onClick={() => { close(); onGoLive && onGoLive(); }}
                  variant="outline"
                  className="w-full rounded-none py-5 bg-transparent border-white/15 hover:border-rose-600/60 text-white gap-2"
                  data-testid="mobile-drawer-golive"
                >
                  <Video size={16} /> Go Live
                </Button>
                {onTicket && (
                  <Button
                    onClick={() => { close(); onTicket(); }}
                    variant="outline"
                    className="w-full rounded-none py-5 bg-transparent border-amber-400/40 hover:border-amber-400 text-amber-400 gap-2"
                    data-testid="mobile-drawer-ticket"
                  >
                    <Ticket size={16} /> Daily Ticket
                  </Button>
                )}
                <button
                  data-testid="mobile-drawer-logout"
                  onClick={async () => { await logout(); close(); navigate("/"); }}
                  className="w-full flex items-center justify-center gap-2 pt-3 text-sm text-zinc-500 hover:text-rose-500 transition-colors"
                >
                  <LogOut size={16} /> Log out
                </button>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
